import { Fragment, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { ShopTuningShopHint } from '~/components/ui/ShopTuningShopHint';
import { Text } from '~/components/ui/Text';
import { useZoomFactor } from '~/hooks';
import { VehicleTechTuningItemConfig } from '~/rage/services/TechTuningService';
import { TechTuningCategory, TechTuningRarity } from '~/shared';
import { CategoryImages, CategoryItemImages } from '../CategoryTab';
import { ItemColor, TuningShopRarityGradientColors } from '../ItemColor';
import {
	StyledBoost,
	StyledBootsWrapper,
	StyledContainer,
} from './SetupTuningCell.styled';

interface SetupTuningCellProps {
	item?: VehicleTechTuningItemConfig;
	category: TechTuningCategory;
	onDragOver?: (e: React.DragEvent) => void;
	onDrop?: (e: React.DragEvent) => void;
	canDrop?: boolean;
	onDragStart?: (e: React.DragEvent) => void;
	onDragEnd?: () => void;
}

const SetupTuningCell = ({
	item,
	category,
	onDragOver,
	onDrop,
	canDrop,
	onDragStart,
	onDragEnd,
}: SetupTuningCellProps) => {
	const [hintVisible, setHintVisible] = useState(false);
	const [hintPosition, setHintPosition] = useState<{
		top: number;
		right: number;
	}>({ top: 0, right: 0 });
	const containerRef = useRef<HTMLDivElement>(null);
	const zoomFactor = useZoomFactor();
	const placeholderImage = useMemo(() => {
		return CategoryImages[category];
	}, [category]);

	const showHint = () => {
		if (containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect();
			setHintPosition({
				top:
					rect.top * (1 / zoomFactor) +
					(rect.height * (1 / zoomFactor) - 80) / 2,
				right: (window.innerWidth - rect.left) * (1 / zoomFactor) + 10,
			});
		}
		setHintVisible(true);
	};

	const hideHint = () => {
		setHintVisible(false);
	};

	const handleDragStart = (e: React.DragEvent) => {
		hideHint();
		if (e.dataTransfer.setDragImage) {
			const baseSize = 60;
			const scaledSize = baseSize * zoomFactor;
			const background =
				TuningShopRarityGradientColors[
					item?.rarity || TechTuningRarity.COMMON
				] || 'rgba(255, 255, 255, 0.02)';

			const dragImage = document.createElement('div');
			dragImage.className = 'drag-image';
			dragImage.style.width = `${scaledSize}px`;
			dragImage.style.height = `${scaledSize}px`;
			dragImage.style.background = background;
			document.body.appendChild(dragImage);

			const imageSize = 40 * zoomFactor;

			if (item && CategoryItemImages[item.category]) {
				const img = document.createElement('img');
				img.src = CategoryItemImages[item.category] || '';
				img.className = 'drag-image-icon';
				img.style.width = `${imageSize}px`;
				img.style.height = `${imageSize}px`;
				dragImage.appendChild(img);
			}

			e.dataTransfer.setDragImage(dragImage, scaledSize / 2, scaledSize / 2);

			setTimeout(() => {
				document.body.removeChild(dragImage);
			}, 0);
		}
		onDragStart?.(e);
	};

	const handleDrop = (e: React.DragEvent) => {
		hideHint();
		onDrop?.(e);
	};

	return (
		<Fragment>
			{createPortal(
				<CSSTransition
					in={hintVisible}
					classNames={'hint'}
					timeout={300}
					unmountOnExit
				>
					<ShopTuningShopHint
						name={item?.name || ''}
						properties={item?.properties || undefined}
						style={{
							top: hintPosition.top,
							right: hintPosition.right,
							position: 'fixed',
						}}
					/>
				</CSSTransition>,
				document.body,
			)}
			<StyledContainer
				ref={containerRef}
				$active={!!item}
				$helpHover={!!canDrop}
				onDragOver={onDragOver}
				onDrop={handleDrop}
				draggable={!!item}
				onDragStart={handleDragStart}
				onDragEnd={onDragEnd}
				onMouseEnter={item ? showHint : undefined}
				onMouseLeave={item ? hideHint : undefined}
				data-tuning-category={category}
			>
				<img src={placeholderImage} className="placeholder" />
				{item && (
					<Fragment>
						<ItemColor
							image={CategoryItemImages[item.category] || ''}
							size={60}
							imageSize={40}
							color={
								TuningShopRarityGradientColors[
									item.rarity || TechTuningRarity.COMMON
								]
							}
						/>
						{item?.properties?.boost && (
							<StyledBootsWrapper>
								<StyledBoost>
									<Text>+{item?.properties?.boost}%</Text>
								</StyledBoost>
							</StyledBootsWrapper>
						)}
					</Fragment>
				)}
			</StyledContainer>
		</Fragment>
	);
};

export { SetupTuningCell };
